import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Tooltip, Checkbox } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaCheck, FaTimes, FaPenAlt, FaUsers, FaBox, FaBell } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import Alerty from '../common/Alerty';
import PaymentTypeEditModal from './PaymentTypeEditModal';
const { rootUrl } = require('../../config/config');

const OwnerPaymentTypes = () => {
    const [activeTab, setActiveTab] = useState('Owners');
    const [ownerPaymentTypes, setOwnerPaymentTypes] = useState([]);
    const [partsPaymentTypes, setPartsPaymentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Modal states
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingPaymentType, setEditingPaymentType] = useState(null);
    const [editingType, setEditingType] = useState(''); // 'owner' or 'parts'

    // Owner payment type states for new entries
    const [ownerNewType, setOwnerNewType] = useState('');
    const [ownerNewShortDesc, setOwnerNewShortDesc] = useState('');
    const [ownerNewLongDesc, setOwnerNewLongDesc] = useState('');
    const [ownerNewSign, setOwnerNewSign] = useState('');
    const [ownerNewDriverPost, setOwnerNewDriverPost] = useState(false);
    const [ownerNewDriverPayType, setOwnerNewDriverPayType] = useState('');
    const [ownerNewSubType, setOwnerNewSubType] = useState('');
    const [ownerNewDef, setOwnerNewDef] = useState(false);
    const [ownerNewNotify, setOwnerNewNotify] = useState(false);

    // Parts payment type states for new entries
    const [partsNewType, setPartsNewType] = useState('');
    const [partsNewShortDesc, setPartsNewShortDesc] = useState('');
    const [partsNewLongDesc, setPartsNewLongDesc] = useState('');
    const [partsNewSign, setPartsNewSign] = useState('');
    const [partsNewSubType, setPartsNewSubType] = useState('');
    const [partsNewDef, setPartsNewDef] = useState(false);
    const [partsNewNotify, setPartsNewNotify] = useState(false);
    const [partsNewDriverPost, setPartsNewDriverPost] = useState(false);
    const [partsNewDriverPayType, setPartsNewDriverPayType] = useState('');

    const { user } = useAuth();

    // Owner payment type functions
    const createOwnerPaymentType = async (e) => {
        e.preventDefault();
        if (!ownerNewType.trim() || !ownerNewShortDesc.trim() || !ownerNewLongDesc.trim() || !ownerNewSign.trim()) {
            setError('Type, Short Description, Long Description, and Sign are required.');
            return;
        }
        try {
            await axios.post(`${rootUrl}/payments/owner-types`, {
                type: ownerNewType.toUpperCase(),
                short_desc: ownerNewShortDesc.toUpperCase(),
                long_desc: ownerNewLongDesc,
                sign: ownerNewSign,
                driver_post: ownerNewDriverPost,
                driver_pay_type: ownerNewDriverPayType.toUpperCase(),
                sub_type: ownerNewSubType.toUpperCase(),
                def: ownerNewDef,
                notify: ownerNewNotify,
                input_by: user.username
            });
            // clear inputs
            setOwnerNewType('');
            setOwnerNewShortDesc('');
            setOwnerNewLongDesc('');
            setOwnerNewSign('');
            setOwnerNewDriverPost(false);
            setOwnerNewDriverPayType('');
            setOwnerNewSubType('');
            setOwnerNewDef(false);
            setOwnerNewNotify(false);
            setError('');
            fetchOwnerPaymentTypes();
        } catch (err) {
            setError('Failed to create new owner payment type.');
        }
    };

    const fetchOwnerPaymentTypes = async () => {
        try {
            const response = await axios.get(`${rootUrl}/payments/owner-types`);
            setOwnerPaymentTypes(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch owner payment types.');
        }
    };

    const openEditModal = (paymentType, type) => {
        setEditingPaymentType(paymentType);
        setEditingType(type);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingPaymentType(null);
        setEditingType('');
    };

    const handleSaveEdit = async (type, data) => {
        try {
            const endpoint = editingType === 'owner' ? 'owner-types' : 'parts-types';
            await axios.put(`${rootUrl}/payments/${endpoint}/${type}`, data);

            // Refresh the appropriate data
            if (editingType === 'owner') {
                await fetchOwnerPaymentTypes();
            } else {
                await fetchPartsPaymentTypes();
            }

            setError('');
        } catch (err) {
            setError(`Failed to update ${editingType} payment type.`);
        }
    };

    const deleteOwnerPaymentType = async (type) => {
        if (!window.confirm('Are you sure you want to delete this owner payment type?')) return;
        try {
            await axios.delete(`${rootUrl}/payments/owner-types/${type}`);
            fetchOwnerPaymentTypes();
        } catch (err) {
            setError('Failed to delete owner payment type.');
        }
    };

    // Parts payment type functions
    const createPartsPaymentType = async (e) => {
        e.preventDefault();
        if (!partsNewType.trim() || !partsNewShortDesc.trim() || !partsNewLongDesc.trim() || !partsNewSign.trim()) {
            setError('Type, Short Description, Long Description, and Sign are required.');
            return;
        }
        try {
            await axios.post(`${rootUrl}/payments/parts-types`, {
                type: partsNewType.toUpperCase(),
                short_desc: partsNewShortDesc.toUpperCase(),
                long_desc: partsNewLongDesc,
                sign: partsNewSign,
                sub_type: partsNewSubType.toUpperCase(),
                def: partsNewDef,
                notify: partsNewNotify,
                driver_post: partsNewDriverPost,
                driver_pay_type: partsNewDriverPayType.toUpperCase(),
                input_by: user.username
            });
            // clear inputs
            setPartsNewType('');
            setPartsNewShortDesc('');
            setPartsNewLongDesc('');
            setPartsNewSign('');
            setPartsNewSubType('');
            setPartsNewDef(false);
            setPartsNewNotify(false);
            setPartsNewDriverPost(false);
            setPartsNewDriverPayType('');
            setError('');
            fetchPartsPaymentTypes();
        } catch (err) {
            setError('Failed to create new parts payment type.');
        }
    };

    const fetchPartsPaymentTypes = async () => {
        try {
            const response = await axios.get(`${rootUrl}/payments/parts-types`);
            setPartsPaymentTypes(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch parts payment types.');
        }
    };

    const deletePartsPaymentType = async (type) => {
        if (!window.confirm('Are you sure you want to delete this parts payment type?')) return;
        try {
            await axios.delete(`${rootUrl}/payments/parts-types/${type}`);
            fetchPartsPaymentTypes();
        } catch (err) {
            setError('Failed to delete parts payment type.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([fetchOwnerPaymentTypes(), fetchPartsPaymentTypes()]);
            setLoading(false);
        };
        fetchData();
    }, []);

    const renderOwnerPaymentTypes = () => (
        <>
            {/* Add New Owner Payment Type Form */}
            <Card className="mb-6 shadow-lg border border-gray-100">
                <CardBody className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-purple-50 rounded-lg">
                            <FaPlus className="text-purple-600" />
                        </div>
                        <Typography variant="h6" color="blue-gray">
                            Add New Owner Payment Type
                        </Typography>
                    </div>
                    <form onSubmit={createOwnerPaymentType}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <Input
                                type="text"
                                label="Type Code"
                                value={ownerNewType}
                                onChange={e => setOwnerNewType(e.target.value.slice(0, 10).toUpperCase())}
                                maxLength={10}
                                color="purple"
                            />
                            <Input
                                type="text"
                                label="Short Description"
                                value={ownerNewShortDesc}
                                onChange={e => setOwnerNewShortDesc(e.target.value.toUpperCase())}
                                color="purple"
                            />
                            <Input
                                type="text"
                                label="Long Description"
                                value={ownerNewLongDesc}
                                onChange={e => setOwnerNewLongDesc(e.target.value)}
                                color="purple"
                            />
                            <Input
                                type="text"
                                label="Sign (+/-)"
                                value={ownerNewSign}
                                onChange={e => setOwnerNewSign(e.target.value.slice(0, 1))}
                                maxLength={1}
                                color="purple"
                            />
                            <Input
                                type="text"
                                label="Driver Pay Type"
                                value={ownerNewDriverPayType}
                                onChange={e => setOwnerNewDriverPayType(e.target.value.toUpperCase())}
                                color="purple"
                            />
                            <Input
                                type="text"
                                label="Sub Type"
                                value={ownerNewSubType}
                                onChange={e => setOwnerNewSubType(e.target.value.toUpperCase())}
                                color="purple"
                            />
                            <div className="lg:col-span-2 flex flex-wrap items-center gap-6">
                                <Checkbox
                                    label="Driver Post"
                                    checked={ownerNewDriverPost}
                                    onChange={e => setOwnerNewDriverPost(e.target.checked)}
                                    color="purple"
                                />
                                <Checkbox
                                    label="Default"
                                    checked={ownerNewDef}
                                    onChange={e => setOwnerNewDef(e.target.checked)}
                                    color="purple"
                                />
                                <Checkbox
                                    label="Notify"
                                    checked={ownerNewNotify}
                                    onChange={e => setOwnerNewNotify(e.target.checked)}
                                    color="purple"
                                />
                                <Button
                                    type="submit"
                                    color="purple"
                                    className="ml-auto flex items-center gap-2"
                                >
                                    <FaPlus /> Add Type
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card className="shadow-lg border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">#</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Description</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sign</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Driver Post</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sub Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Settings</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Actions</Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {ownerPaymentTypes && Array.isArray(ownerPaymentTypes) && ownerPaymentTypes.map((paymentType, index) => (
                                <tr key={paymentType.type} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <Typography variant="small" color="blue-gray" className="font-normal">{index + 1}</Typography>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.type}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.short_desc}</Typography>
                                            <Typography variant="small" color="gray" className="font-normal opacity-70">{paymentType.long_desc}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${paymentType.sign === '+' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {paymentType.sign}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        {paymentType.driver_post ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <FaCheck className="text-green-500" />
                                                <span className="text-xs text-gray-500">{paymentType.driver_pay_type}</span>
                                            </div>
                                        ) : (
                                            <FaTimes className="text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
                                            {paymentType.sub_type || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            {paymentType.def && (
                                                <Tooltip content="Default">
                                                    <span className="p-1 bg-blue-50 text-blue-600 rounded"><FaCheck size={12} /></span>
                                                </Tooltip>
                                            )}
                                            {paymentType.notify && (
                                                <Tooltip content="Notifications Enabled">
                                                    <span className="p-1 bg-amber-50 text-amber-600 rounded"><FaBell size={12} /></span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            <Tooltip content="Edit">
                                                <Button size="sm" variant="text" color="blue-gray" className="p-2 rounded-full hover:bg-gray-100" onClick={() => openEditModal(paymentType, 'owner')}>
                                                    <FaPenAlt />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip content="Delete">
                                                <Button size="sm" variant="text" color="red" className="p-2 rounded-full hover:bg-red-50" onClick={() => deleteOwnerPaymentType(paymentType.type)}>
                                                    <FaTrash />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );

    const renderPartsPaymentTypes = () => (
        <>
            {/* Add New Parts Payment Type Form */}
            <Card className="mb-6 shadow-lg border border-gray-100">
                <CardBody className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-teal-50 rounded-lg">
                            <FaPlus className="text-teal-600" />
                        </div>
                        <Typography variant="h6" color="blue-gray">
                            Add New Parts Payment Type
                        </Typography>
                    </div>
                    <form onSubmit={createPartsPaymentType}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <Input
                                type="text"
                                label="Type Code"
                                value={partsNewType}
                                onChange={e => setPartsNewType(e.target.value.slice(0, 10).toUpperCase())}
                                maxLength={10}
                                color="teal"
                            />
                            <Input
                                type="text"
                                label="Short Description"
                                value={partsNewShortDesc}
                                onChange={e => setPartsNewShortDesc(e.target.value.toUpperCase())}
                                color="teal"
                            />
                            <Input
                                type="text"
                                label="Long Description"
                                value={partsNewLongDesc}
                                onChange={e => setPartsNewLongDesc(e.target.value)}
                                color="teal"
                            />
                            <Input
                                type="text"
                                label="Sign (+/-)"
                                value={partsNewSign}
                                onChange={e => setPartsNewSign(e.target.value.slice(0, 1))}
                                maxLength={1}
                                color="teal"
                            />
                            <Input
                                type="text"
                                label="Sub Type"
                                value={partsNewSubType}
                                onChange={e => setPartsNewSubType(e.target.value.toUpperCase())}
                                color="teal"
                            />
                            {partsNewDriverPost && (
                                <Input
                                    type="text"
                                    label="Driver Pay Type"
                                    value={partsNewDriverPayType}
                                    onChange={e => setPartsNewDriverPayType(e.target.value.toUpperCase())}
                                    color="teal"
                                />
                            )}
                            <div className="lg:col-span-2 flex flex-wrap items-center gap-6">
                                <Checkbox
                                    label="Driver Post"
                                    checked={partsNewDriverPost}
                                    onChange={e => setPartsNewDriverPost(e.target.checked)}
                                    color="teal"
                                />
                                <Checkbox
                                    label="Default"
                                    checked={partsNewDef}
                                    onChange={e => setPartsNewDef(e.target.checked)}
                                    color="teal"
                                />
                                <Checkbox
                                    label="Notify"
                                    checked={partsNewNotify}
                                    onChange={e => setPartsNewNotify(e.target.checked)}
                                    color="teal"
                                />
                                <Button
                                    type="submit"
                                    color="teal"
                                    className="ml-auto flex items-center gap-2"
                                >
                                    <FaPlus /> Add Type
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardBody>
            </Card>

            <Card className="shadow-lg border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">#</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Description</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sign</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Driver Post</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Sub Type</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Settings</Typography>
                                </th>
                                <th className="p-4 border-b border-blue-gray-50 text-center">
                                    <Typography variant="small" color="blue-gray" className="font-bold leading-none opacity-70">Actions</Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {partsPaymentTypes && Array.isArray(partsPaymentTypes) && partsPaymentTypes.map((paymentType, index) => (
                                <tr key={paymentType.type} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <Typography variant="small" color="blue-gray" className="font-normal">{index + 1}</Typography>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.type}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex flex-col">
                                            <Typography variant="small" color="blue-gray" className="font-bold">{paymentType.short_desc}</Typography>
                                            <Typography variant="small" color="gray" className="font-normal opacity-70">{paymentType.long_desc}</Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${paymentType.sign === '+' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                            {paymentType.sign}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        {paymentType.driver_post ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <FaCheck className="text-green-500" />
                                                <span className="text-xs text-gray-500">{paymentType.driver_pay_type}</span>
                                            </div>
                                        ) : (
                                            <FaTimes className="text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50 text-center">
                                        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
                                            {paymentType.sub_type || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            {paymentType.def && (
                                                <Tooltip content="Default">
                                                    <span className="p-1 bg-blue-50 text-blue-600 rounded"><FaCheck size={12} /></span>
                                                </Tooltip>
                                            )}
                                            {paymentType.notify && (
                                                <Tooltip content="Notifications Enabled">
                                                    <span className="p-1 bg-amber-50 text-amber-600 rounded"><FaBell size={12} /></span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-blue-gray-50">
                                        <div className="flex justify-center gap-2">
                                            <Tooltip content="Edit">
                                                <Button size="sm" variant="text" color="blue-gray" className="p-2 rounded-full hover:bg-gray-100" onClick={() => openEditModal(paymentType, 'parts')}>
                                                    <FaPenAlt />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip content="Delete">
                                                <Button size="sm" variant="text" color="red" className="p-2 rounded-full hover:bg-red-50" onClick={() => deletePartsPaymentType(paymentType.type)}>
                                                    <FaTrash />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-3">Owner & Parts Transaction Types</h1>
                    <div className="w-24 h-1.5 bg-[#FF5733] mx-auto rounded-full shadow-sm"></div>
                    <Typography className="mt-4 text-gray-600 font-medium">
                        Manage payment classifications for owners and parts inventory
                    </Typography>
                </div>

                {error && (
                    <div className='mb-6'>
                        <Alerty color='orange' message={error} setOpen={setError} />
                    </div>
                )}

                <div className="mb-8">
                    <div className="flex justify-center p-1 bg-white/50 backdrop-blur-sm rounded-xl shadow-sm w-fit mx-auto">
                        <button
                            type="button"
                            onClick={() => setActiveTab('Owners')}
                            className={`flex items-center gap-2 py-3 px-8 rounded-lg transition-all duration-300 font-semibold ${activeTab === 'Owners'
                                    ? 'bg-white text-purple-600 shadow-md transform scale-105'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                                }`}
                        >
                            <FaUsers className="text-lg" />
                            <span>Owners</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('Parts')}
                            className={`flex items-center gap-2 py-3 px-8 rounded-lg transition-all duration-300 font-semibold ${activeTab === 'Parts'
                                    ? 'bg-white text-teal-600 shadow-md transform scale-105'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                                }`}
                        >
                            <FaBox className="text-lg" />
                            <span>Parts</span>
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5733]"></div>
                    </div>
                ) : (
                    <div className="animate-fade-in-up">
                        {activeTab === 'Owners' && renderOwnerPaymentTypes()}
                        {activeTab === 'Parts' && renderPartsPaymentTypes()}
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            <PaymentTypeEditModal
                isOpen={isEditModalOpen}
                onClose={closeEditModal}
                paymentType={editingPaymentType}
                type={editingType}
                onSave={handleSaveEdit}
                user={user}
            />
        </div>
    );
};

export default OwnerPaymentTypes;
